import React, { useState } from 'react';
import {
	Alert,
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Stack,
	TextField,
	Typography,
} from '@mui/material';
import { IInvoice } from '../../types';
import { issueInvoice } from '../../services';
import { extractErrorMessage } from '../../../quotation/helpers';

interface Props {
	invoice: IInvoice;
	open: boolean;
	onClose: () => void;
	onIssued: (invoice: IInvoice) => void;
}

export const IssueInvoiceDialog = ({ invoice, open, onClose, onIssued }: Props): JSX.Element => {
	const [externalReference, setExternalReference] = useState('');
	const [pdfUrl, setPdfUrl] = useState('');
	const [submitting, setSubmitting] = useState(false);
	const [error, setError] = useState<string | null>(null);

	const handleConfirm = async (): Promise<void> => {
		if (!externalReference.trim()) {
			setError('La referencia externa es obligatoria.');
			return;
		}
		setSubmitting(true);
		setError(null);
		try {
			const updated = await issueInvoice(invoice.id, {
				externalReference: externalReference.trim(),
				pdfUrl: pdfUrl.trim() || undefined,
			});
			onIssued(updated);
			onClose();
		} catch (error) {
			setError(extractErrorMessage(error, 'No se pudo emitir la factura.'));
		} finally {
			setSubmitting(false);
		}
	};

	return (
		<Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
			<DialogTitle>Emitir factura {invoice.invoiceNumber}</DialogTitle>
			<DialogContent>
				<Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
					Ingresá el número fiscal del facturador electrónico (CAE / Timbrado / CUFE) y la URL del PDF emitido.
				</Typography>
				<Stack spacing={2} sx={{ mt: 1 }}>
					<TextField
						label="Referencia externa (CAE / Timbrado / CUFE)"
						value={externalReference}
						onChange={(e) => setExternalReference(e.target.value)}
						fullWidth
						required
						autoFocus
					/>
					<TextField
						label="URL del PDF"
						value={pdfUrl}
						onChange={(e) => setPdfUrl(e.target.value)}
						fullWidth
						placeholder="https://cdn.mevuelo.com/..."
					/>
					{error && <Alert severity="error">{error}</Alert>}
				</Stack>
			</DialogContent>
			<DialogActions>
				<Button onClick={onClose} disabled={submitting}>
					Cancelar
				</Button>
				<Button onClick={handleConfirm} variant="contained" disabled={submitting}>
					Emitir
				</Button>
			</DialogActions>
		</Dialog>
	);
};
